
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import {
  getContentSettings,
  updateContentSettings,
  syncSocialAccounts,
  getSocialAccountStatistics,
  getSocialAccountStatisticsHistory,
  startSocialConnect,
  getSocialConnectRedirect,
  getSocialConnectCandidates,
  completeSocialConnect,
  disconnectSocialAccount,
  updateSocialAccount
} from "@/api/ghlContent.api.js";
import { listAiAgents } from "@/api/aiAgents.api.js";
import {
  SUPPORTED_SOCIAL_PLATFORMS,
  formatPlatformLabel,
  extractConnectToken,
  openConnectPopup,
  waitForSocialOAuthMessage
} from "@/utils/ghlSocialConnect.js";
import { accountOptionKey } from "@/utils/socialPlatformMeta.js";
import SocialAccountAvatar from "@/components/social/SocialAccountAvatar.vue";

export default defineComponent({
  name: "social-fanout-settings",
  components: {
    SocialAccountAvatar
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      syncing: false,
      statsLoading: false,
      historyLoading: false,
      connectingPlatform: "",
      disconnectingKey: "",
      savingFanoutKey: "",
      savingAgentKey: "",
      loadError: "",
      statsError: "",
      socialFanoutOptIn: false,
      hasGhlLocation: false,
      socialAccounts: [] as any[],
      availableAgents: [] as any[],
      statsTotals: {} as Record<string, number | null>,
      statsAccounts: [] as any[],
      statsFetchedAt: "" as string,
      historyPoints: [] as any[],
      selectedHistoryMetric: "impressions",
      platformOptions: SUPPORTED_SOCIAL_PLATFORMS,
      kpiCards: [
        { key: "posts", label: "Posts" },
        { key: "likes", label: "Likes" },
        { key: "comments", label: "Comments" },
        { key: "impressions", label: "Impressions" },
        { key: "reach", label: "Reach" }
      ]
    };
  },
  async mounted() {
    await Promise.all([this.loadSettings(), this.loadAgents()]);
    this.loading = false;
  },
  computed: {
    chartHistorySeries(): any[] {
      const key = this.selectedHistoryMetric;
      const label = this.kpiCards.find((kpi) => kpi.key === key)?.label || key;
      return [
        {
          name: label,
          data: this.historyPoints.map((point) => point[key] ?? null)
        }
      ];
    },
    chartHistoryOptions(): any {
      const key = this.selectedHistoryMetric;
      const label = this.kpiCards.find((kpi) => kpi.key === key)?.label || key;
      const categories = this.historyPoints.map((point) => {
        const date = new Date(point.at);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      });

      return {
        chart: {
          type: "area",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit"
        },
        colors: ["#009EF7"],
        dataLabels: { enabled: false },
        stroke: {
          curve: "smooth",
          width: 3
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        grid: {
          borderColor: "#ECEEF1",
          strokeDashArray: 4,
          yaxis: { lines: { show: true } }
        },
        xaxis: {
          categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: { colors: "#8A9299", fontSize: "11px" }
          }
        },
        yaxis: {
          labels: {
            style: { colors: "#8A9299", fontSize: "11px" },
            formatter: (value: number) => {
              if (value == null || Number.isNaN(Number(value))) return "";
              return Number(value).toLocaleString();
            }
          }
        },
        tooltip: {
          x: { show: true },
          y: {
            formatter: (value: number) => {
              if (value == null || Number.isNaN(Number(value))) return "—";
              return `${label}: ${Number(value).toLocaleString()}`;
            }
          }
        }
      };
    }
  },
  methods: {
    formatPlatformLabel,
    accountKey: accountOptionKey,
    formatMetric(value: number | null | undefined) {
      if (value == null || Number.isNaN(Number(value))) return "—";
      return Number(value).toLocaleString();
    },
    formatDate(value: string) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString();
    },
    agentLabel(agent: any) {
      if (agent.knowledgeBaseName) {
        return `${agent.name} (${agent.knowledgeBaseName})`;
      }
      return agent.name;
    },
    async loadAgents() {
      try {
        const response = await listAiAgents();
        this.availableAgents = response.data?.agents || [];
      } catch {
        this.availableAgents = [];
      }
    },
    async loadSettings() {
      try {
        const response = await getContentSettings();
        this.socialFanoutOptIn = Boolean(response.data.socialFanoutOptIn);
        this.hasGhlLocation = Boolean(response.data.hasGhlLocation);
        this.socialAccounts = response.data.socialAccounts || [];
        this.loadError = "";
        if (this.hasGhlLocation && this.socialAccounts.length) {
          if (this.socialAccounts.some((account) => !account.avatar)) {
            await this.refreshAccounts({ silent: true });
          }
          await Promise.all([this.loadStatistics(false), this.loadHistory()]);
        } else {
          this.statsTotals = {};
          this.statsAccounts = [];
          this.statsFetchedAt = "";
          this.statsError = "";
          this.historyPoints = [];
        }
      } catch (e) {
        this.loadError = e?.response?.data?.message || "Unable to load settings.";
      }
    },
    async loadStatistics(refresh = false) {
      if (!this.hasGhlLocation || !this.socialAccounts.length) return;
      this.statsLoading = true;
      this.statsError = "";
      try {
        const response = await getSocialAccountStatistics(refresh ? { refresh: 1 } : {});
        this.statsTotals = response.data.totals || {};
        this.statsAccounts = response.data.accounts || [];
        this.statsFetchedAt = response.data.fetchedAt || "";
        if (refresh) {
          await this.loadHistory();
        }
      } catch (e) {
        this.statsError = e?.response?.data?.message || "Unable to load social statistics.";
      } finally {
        this.statsLoading = false;
      }
    },
    async loadHistory() {
      if (!this.hasGhlLocation || !this.socialAccounts.length) {
        this.historyPoints = [];
        return;
      }
      this.historyLoading = true;
      try {
        const response = await getSocialAccountStatisticsHistory({ days: 30 });
        this.historyPoints = response.data.points || [];
      } catch {
        this.historyPoints = [];
      } finally {
        this.historyLoading = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      try {
        const response = await updateContentSettings({
          socialFanoutOptIn: this.socialFanoutOptIn
        });
        this.socialFanoutOptIn = Boolean(response.data.socialFanoutOptIn);
        Swal.fire({
          title: "",
          text: this.socialFanoutOptIn ? "Social fan-out enabled." : "Social fan-out disabled.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        await this.loadSettings();
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to save settings.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.saving = false;
      }
    },
    async refreshAccounts(options: { silent?: boolean } = {}) {
      const silent = Boolean(options.silent);
      this.syncing = true;
      try {
        const response = await syncSocialAccounts();
        this.socialAccounts = response.data.accounts || [];
        if (this.socialAccounts.length) {
          await Promise.all([this.loadStatistics(false), this.loadHistory()]);
        }
        if (!silent) {
          Swal.fire({
            title: "",
            text: "Social accounts synced.",
            icon: "success",
            confirmButtonClass: "btn btn-secondary"
          });
        }
      } catch (e) {
        if (!silent) {
          Swal.fire({
            title: "",
            text: e?.response?.data?.message || "Unable to sync social accounts.",
            icon: "error",
            confirmButtonClass: "btn btn-secondary"
          });
        }
      } finally {
        this.syncing = false;
      }
    },
    async connectPlatform(platform) {
      this.connectingPlatform = platform;
      let popup: Window | null = null;

      try {
        const oauthPromise = waitForSocialOAuthMessage();
        const sessionResponse = await startSocialConnect(platform);
        const token = extractConnectToken(sessionResponse.data.connectUrl);
        if (!token) {
          throw new Error("Unable to start social connect session.");
        }

        const redirectResponse = await getSocialConnectRedirect(platform, token);
        const redirectUrl = redirectResponse.data.redirectUrl;
        if (!redirectUrl) {
          throw new Error("Unable to resolve social OAuth redirect URL.");
        }

        popup = openConnectPopup(redirectUrl);
        if (!popup) {
          throw new Error("Popup blocked. Allow popups for this site and try again.");
        }

        const oauthResult = await oauthPromise;
        try {
          popup.close();
        } catch {
          // ignore cross-origin close errors
        }

        const candidatesResponse = await getSocialConnectCandidates(
          platform,
          oauthResult.accountId
        );
        const candidates = candidatesResponse.data.candidates || [];
        if (!candidates.length) {
          throw new Error("No accounts or pages were returned for this platform.");
        }

        const selection = await this.pickCandidate(platform, candidates);
        if (!selection) return;

        const completeResponse = await completeSocialConnect(platform, {
          accountId: oauthResult.accountId,
          selection
        });
        this.socialAccounts = completeResponse.data.accounts || [];
        if (this.socialAccounts.length) {
          await Promise.all([this.loadStatistics(false), this.loadHistory()]);
        }
        Swal.fire({
          title: "",
          text: `${formatPlatformLabel(platform)} connected successfully.`,
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch {
            // ignore
          }
        }
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || e?.message || "Unable to connect social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.connectingPlatform = "";
      }
    },
    async pickCandidate(platform, candidates) {
      if (candidates.length === 1) {
        return candidates[0];
      }

      const inputOptions = candidates.reduce((acc, candidate) => {
        acc[candidate.id] = candidate.name;
        return acc;
      }, {});

      const result = await Swal.fire({
        title: `Select ${formatPlatformLabel(platform)} account`,
        input: "select",
        inputOptions,
        inputPlaceholder: "Choose an account",
        showCancelButton: true,
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-secondary"
      });

      if (!result.isConfirmed || !result.value) return null;
      return candidates.find((candidate) => candidate.id === result.value) || null;
    },
    async disconnectAccount(account) {
      const platform = account.platform;
      const accountName = account.accountName || account.accountId;
      const confirm = await Swal.fire({
        title: `Disconnect ${formatPlatformLabel(platform)}?`,
        text: `This removes ${accountName} from social fan-out.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Disconnect",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-secondary"
      });

      if (!confirm.isConfirmed) return;

      const key = accountOptionKey(account);
      this.disconnectingKey = key;
      try {
        const response = await disconnectSocialAccount(platform, account.accountId);
        this.socialAccounts = response.data.accounts || [];
        Swal.fire({
          title: "",
          text: `${accountName} disconnected.`,
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to disconnect social account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.disconnectingKey = "";
      }
    },
    async saveAccountFanout(account, event) {
      const fanoutEnabled = Boolean((event.target as HTMLInputElement)?.checked);
      const key = accountOptionKey(account);
      this.savingFanoutKey = key;
      try {
        const response = await updateSocialAccount(
          account.platform,
          account.accountId,
          { fanoutEnabled }
        );
        this.socialAccounts = response.data.accounts || [];
      } catch (e) {
        await this.loadSettings();
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to save account fan-out setting.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.savingFanoutKey = "";
      }
    },
    async saveAccountAgent(account, event) {
      const rawValue = (event.target as HTMLSelectElement)?.value;
      const agentId = rawValue ? Number(rawValue) : null;
      const key = accountOptionKey(account);
      this.savingAgentKey = key;
      try {
        const response = await updateSocialAccount(
          account.platform,
          account.accountId,
          {
            agentId,
            fanoutEnabled: account.fanoutEnabled !== false
          }
        );
        this.socialAccounts = response.data.accounts || [];
      } catch (e) {
        await this.loadSettings();
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to save AI agent for this account.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.savingAgentKey = "";
      }
    }
  }
});
